import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button, CircularProgress, IconButton, InputAdornment } from "@mui/material";
import { FaEnvelope, FaSun, FaMoon, FaArrowLeft, FaCheckCircle, FaLock, FaShieldAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";

const ForgotPasswordForm = () => {
  const navigate              = useNavigate();
  const [email, setEmail]     = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState("");
  const [isDark, setIsDark]   = useState(() => localStorage.getItem("theme") === "dark");

  useEffect(() => {
    const html = document.querySelector("html");
    if (!html) return;
    const obs = new MutationObserver(() => setIsDark(html.classList.contains("dark-theme")));
    obs.observe(html, { attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  const toggleTheme = () => {
    const html = document.querySelector("html");
    const next = html.classList.contains("dark-theme") ? "light" : "dark";
    if (next === "dark") { html.classList.add("dark-theme"); localStorage.setItem("theme", "dark"); }
    else { html.classList.remove("dark-theme"); localStorage.setItem("theme", "light"); }
    setIsDark(next === "dark");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setError("");
    setLoading(true);
    try {
      await api.post("/api/admin/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const bg = isDark
    ? "linear-gradient(135deg,#0f172a 0%,#1e1b4b 60%,#0f172a 100%)"
    : "linear-gradient(135deg,#eef2ff 0%,#f5f3ff 50%,#ecfdf5 100%)";

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: bg, p: 2, position: "relative", overflow: "hidden" }}>

      {/* Background orbs */}
      <Box sx={{ position:"fixed", top:-120, left:-120, width:400, height:400, borderRadius:"50%", background:"radial-gradient(circle,rgba(99,102,241,0.18) 0%,transparent 70%)", pointerEvents:"none" }} />
      <Box sx={{ position:"fixed", bottom:-150, right:-100, width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle,rgba(139,92,246,0.13) 0%,transparent 70%)", pointerEvents:"none" }} />

      {/* Theme toggle */}
      <IconButton onClick={toggleTheme} sx={{
        position:"fixed", top:20, right:20, zIndex:10,
        bgcolor: isDark ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.85)",
        backdropFilter:"blur(10px)",
        color: isDark ? "#fbbf24" : "#6366f1",
        border:`1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(99,102,241,0.18)"}`,
        "&:hover":{ bgcolor: isDark ? "rgba(255,255,255,0.13)" : "#fff" },
        width:40, height:40,
      }}>
        {isDark ? <FaSun size={15}/> : <FaMoon size={15}/>}
      </IconButton>

      {/* Main card */}
      <Box sx={{
        width:"100%", maxWidth:900,
        display:"flex", borderRadius:"24px", overflow:"hidden",
        boxShadow: isDark ? "0 32px 100px rgba(0,0,0,0.6)" : "0 32px 100px rgba(99,102,241,0.15)",
        border:`1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(99,102,241,0.1)"}`,
        minHeight:560,
      }}>

        {/* LEFT — branding panel */}
        <Box sx={{
          display:{ xs:"none", md:"flex" }, flexDirection:"column",
          justifyContent:"center", alignItems:"center",
          flex:"0 0 42%",
          background: isDark
            ? "linear-gradient(145deg,#1e1b4b,#312e81)"
            : "linear-gradient(145deg,#6366f1,#8b5cf6)",
          p:5, position:"relative", overflow:"hidden",
        }}>
          {/* Decorative circles */}
          <Box sx={{ position:"absolute", top:-60, right:-60, width:200, height:200, borderRadius:"50%", bgcolor:"rgba(255,255,255,0.06)" }} />
          <Box sx={{ position:"absolute", bottom:-80, left:-40, width:240, height:240, borderRadius:"50%", bgcolor:"rgba(255,255,255,0.05)" }} />

          <Box sx={{ width:88, height:88, borderRadius:"24px", bgcolor:"rgba(255,255,255,0.15)", backdropFilter:"blur(10px)", display:"flex", alignItems:"center", justifyContent:"center", mb:3.5, border:"1px solid rgba(255,255,255,0.2)" }}>
            <FaShieldAlt size={40} color="#fff"/>
          </Box>

          <Typography sx={{ fontSize:26, fontWeight:800, color:"#fff", mb:1.5, textAlign:"center", lineHeight:1.2 }}>
            Account Recovery
          </Typography>
          <Typography sx={{ fontSize:14, color:"rgba(255,255,255,0.75)", textAlign:"center", lineHeight:1.8, mb:4 }}>
            Securely reset your admin password with a time-limited link sent to your email.
          </Typography>

          {[
            { icon:<FaLock size={12}/>,  text:"Secure one-time reset link" },
            { icon:<FaCheckCircle size={12}/>, text:"Link expires in 15 minutes" },
            { icon:<FaShieldAlt size={12}/>, text:"All sessions revoked after reset" },
          ].map(({ icon, text }) => (
            <Box key={text} sx={{ display:"flex", alignItems:"center", gap:1.5, mb:1.75, alignSelf:"flex-start" }}>
              <Box sx={{ width:28, height:28, borderRadius:"8px", bgcolor:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, color:"#fff" }}>
                {icon}
              </Box>
              <Typography sx={{ fontSize:13.5, color:"rgba(255,255,255,0.85)", fontWeight:500 }}>{text}</Typography>
            </Box>
          ))}
        </Box>

        {/* RIGHT — form panel */}
        <Box sx={{
          flex:1, display:"flex", flexDirection:"column", justifyContent:"center",
          bgcolor: isDark ? "#0f172a" : "#fff",
          p:{ xs:3.5, sm:5 },
        }}>
          <Button startIcon={<FaArrowLeft size={11}/>} onClick={() => navigate("/sign-in/")}
            sx={{ mb:3.5, textTransform:"none", fontSize:13, color:"#6366f1", p:0, alignSelf:"flex-start",
              "&:hover":{ bgcolor:"transparent", textDecoration:"underline" } }}>
            Back to Sign In
          </Button>

          {!sent ? (
            <>
              <Box sx={{ width:56, height:56, borderRadius:"16px", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", display:"flex", alignItems:"center", justifyContent:"center", mb:3, boxShadow:"0 8px 24px rgba(99,102,241,0.3)" }}>
                <FaLock size={22} color="#fff"/>
              </Box>

              <Typography sx={{ fontSize:26, fontWeight:800, color: isDark ? "#f1f5f9" : "#0f172a", mb:0.75 }}>
                Forgot Password?
              </Typography>
              <Typography sx={{ fontSize:14, color: isDark ? "#94a3b8" : "#64748b", mb:3.5, lineHeight:1.75 }}>
                Enter your admin email and we'll send a secure link to reset your password.
              </Typography>

              <form onSubmit={handleSubmit}>
                <Typography sx={{ fontSize:12, fontWeight:700, color: isDark ? "#cbd5e1" : "#374151", mb:0.75, letterSpacing:"0.4px", textTransform:"uppercase" }}>
                  Email Address
                </Typography>
                <TextField fullWidth size="small" type="email" placeholder="admin@example.com"
                  value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }}
                  error={!!error} helperText={error}
                  sx={{
                    mb:3,
                    "& .MuiOutlinedInput-root":{
                      borderRadius:"12px", fontSize:14,
                      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                      "& fieldset":{ borderColor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" },
                      "&:hover fieldset":{ borderColor:"#6366f1" },
                      "&.Mui-focused fieldset":{ borderColor:"#6366f1", borderWidth:2 },
                    },
                    "& input":{ color: isDark ? "#f1f5f9" : "#0f172a", padding:"11px 14px" },
                  }}
                  InputProps={{ startAdornment:(
                    <InputAdornment position="start">
                      <FaEnvelope size={14} color={isDark ? "#475569" : "#94a3b8"}/>
                    </InputAdornment>
                  )}}
                />

                <Button fullWidth variant="contained" disableElevation type="submit" disabled={loading}
                  sx={{
                    height:50, textTransform:"none", fontWeight:700, fontSize:15, borderRadius:"12px",
                    background:"linear-gradient(135deg,#6366f1,#8b5cf6)",
                    boxShadow:"0 4px 20px rgba(99,102,241,0.4)",
                    "&:hover":{ background:"linear-gradient(135deg,#4f46e5,#7c3aed)", boxShadow:"0 6px 28px rgba(99,102,241,0.5)", transform:"translateY(-1px)" },
                    "&:disabled":{ background:"#c7d2fe", boxShadow:"none" },
                    transition:"all 0.2s",
                  }}>
                  {loading ? <CircularProgress size={20} sx={{ color:"#fff" }}/> : "Send Reset Link"}
                </Button>
              </form>
            </>
          ) : (
            <Box sx={{ textAlign:"center" }}>
              <Box sx={{
                width:80, height:80, borderRadius:"50%",
                background:"linear-gradient(135deg,#10b981,#059669)",
                display:"flex", alignItems:"center", justifyContent:"center",
                mx:"auto", mb:3,
                boxShadow:"0 12px 40px rgba(16,185,129,0.35)",
                animation:"popIn 0.45s cubic-bezier(0.175,0.885,0.32,1.275)",
                "@keyframes popIn":{ "0%":{ transform:"scale(0)", opacity:0 }, "100%":{ transform:"scale(1)", opacity:1 } },
              }}>
                <FaCheckCircle size={36} color="#fff"/>
              </Box>

              <Typography sx={{ fontSize:24, fontWeight:800, color: isDark ? "#f1f5f9" : "#0f172a", mb:1 }}>
                Check Your Inbox!
              </Typography>
              <Typography sx={{ fontSize:14, color: isDark ? "#94a3b8" : "#64748b", lineHeight:1.8, mb:3 }}>
                If <Box component="span" sx={{ fontWeight:700, color:"#6366f1" }}>{email}</Box> is registered, you'll receive a reset link within a few seconds.
              </Typography>

              <Box sx={{ p:2.5, borderRadius:"14px", bgcolor: isDark ? "rgba(99,102,241,0.08)" : "#eef2ff", border:`1px solid ${isDark ? "rgba(99,102,241,0.15)" : "#c7d2fe"}`, mb:3.5, textAlign:"left" }}>
                {["Check your spam/junk folder if not received","Link expires in 15 minutes","Request a new link if it doesn't arrive"].map((tip) => (
                  <Box key={tip} sx={{ display:"flex", alignItems:"center", gap:1.5, mb:1, "&:last-child":{ mb:0 } }}>
                    <Box sx={{ width:6, height:6, borderRadius:"50%", bgcolor:"#6366f1", flexShrink:0 }}/>
                    <Typography sx={{ fontSize:13, color: isDark ? "#a5b4fc" : "#4338ca" }}>{tip}</Typography>
                  </Box>
                ))}
              </Box>

              <Button variant="outlined" fullWidth onClick={() => { setSent(false); setEmail(""); }}
                sx={{ textTransform:"none", borderRadius:"12px", fontSize:14, fontWeight:600, height:46, borderColor:"#6366f1", color:"#6366f1", "&:hover":{ bgcolor:"rgba(99,102,241,0.06)", borderColor:"#4f46e5" } }}>
                Try a Different Email
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPasswordForm;
